import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';

export interface Client {
  id: string;
  name: string;
  email: string;
  company: string;
  createdAt: Date;
  status: 'active' | 'inactive' | 'suspended';
  proxmoxUser: string;
  vms: string[];
}

@Injectable({
  providedIn: 'root'
})
export class ClientManagementService {
  private clients: Client[] = [
    {
      id: '1',
      name: 'John Doe',
      email: 'john@example.com',
      company: 'Tech Solutions Inc.',
      createdAt: new Date('2023-01-15'),
      status: 'active',
      proxmoxUser: 'client1@pve',
      vms: ['vm-100', 'vm-101']
    },
    {
      id: '2',
      name: 'Jane Smith',
      email: 'jane@example.com',
      company: 'Cloud Innovations Ltd.',
      createdAt: new Date('2023-03-22'),
      status: 'active',
      proxmoxUser: 'client2@pve',
      vms: ['vm-200', 'vm-201', 'vm-202']
    },
    {
      id: '3',
      name: 'Robert Johnson',
      email: 'robert@example.com',
      company: 'Data Systems Corp.',
      createdAt: new Date('2023-05-10'),
      status: 'suspended',
      proxmoxUser: 'client3@pve',
      vms: ['vm-300']
    }
  ];

  private clientsSubject = new BehaviorSubject<Client[]>(this.clients);
  public clients$ = this.clientsSubject.asObservable();

  constructor() { }

  getClients(): Observable<Client[]> {
    return of(this.clients);
  }

  getClientById(id: string): Observable<Client | undefined> {
    const client = this.clients.find(c => c.id === id);
    return of(client);
  }

  addClient(client: Omit<Client, 'id' | 'createdAt'>): Observable<Client> {
    const newClient: Client = {
      ...client,
      id: (this.clients.length + 1).toString(),
      createdAt: new Date()
    };
    this.clients.push(newClient);
    this.clientsSubject.next(this.clients);
    return of(newClient);
  }

  updateClient(id: string, client: Partial<Client>): Observable<Client | undefined> {
    const index = this.clients.findIndex(c => c.id === id);
    if (index !== -1) {
      this.clients[index] = { ...this.clients[index], ...client };
      this.clientsSubject.next(this.clients);
      return of(this.clients[index]);
    }
    return of(undefined);
  }

  deleteClient(id: string): Observable<boolean> {
    const index = this.clients.findIndex(c => c.id === id);
    if (index !== -1) {
      this.clients.splice(index, 1);
      this.clientsSubject.next(this.clients);
      return of(true);
    }
    return of(false);
  }
}